import { z } from 'zod';

// ───────────────────────────── Magazyn (docs/14) ─────────────────────────────

/** Rodzaje dokumentów magazynowych: znak ruchu i czy wymagają przyczyny. */
export const STOCK_DOC_KINDS = {
  BO: { label: 'Stan początkowy', short: 'BO', sign: 1, docType: 'mag_bo', needsReason: false, needsPrice: true },
  PZ: { label: 'Przyjęcie', short: 'PZ', sign: 1, docType: 'mag_pz', needsReason: false, needsPrice: true },
  WZ: { label: 'Wydanie na realizację', short: 'WZ', sign: -1, docType: 'mag_wz', needsReason: false, needsPrice: false },
  WE: { label: 'Wydanie na zapas ekipy', short: 'WE', sign: -1, docType: 'mag_we', needsReason: false, needsPrice: false },
  ZW: { label: 'Zwrot', short: 'ZW', sign: 1, docType: 'mag_zw', needsReason: false, needsPrice: false },
  MM: { label: 'Przesunięcie', short: 'MM', sign: -1, docType: 'mag_mm', needsReason: false, needsPrice: false },
  INW: { label: 'Różnice inwentaryzacji', short: 'INW', sign: 0, docType: 'mag_inw', needsReason: true, needsPrice: false },
  KOR: { label: 'Korekta', short: 'KOR', sign: 0, docType: 'mag_kor', needsReason: true, needsPrice: false },
} as const;
export type StockDocKind = keyof typeof STOCK_DOC_KINDS;
export const STOCK_DOC_KIND_KEYS = Object.keys(STOCK_DOC_KINDS) as StockDocKind[];
/** rodzaje tworzone ręcznie w ekranie Dokumenty (INW powstaje z inwentaryzacji) */
export const MANUAL_DOC_KINDS: StockDocKind[] = ['BO', 'PZ', 'WZ', 'WE', 'ZW', 'MM', 'KOR'];

export const STOCK_DOC_STATUSES = { roboczy: 'Roboczy', zatwierdzony: 'Zatwierdzony' } as const;
export type StockDocStatus = keyof typeof STOCK_DOC_STATUSES;

export const STOCK_COUNT_STATUSES = { otwarta: 'W toku', zamknieta: 'Zamknięta', anulowana: 'Anulowana' } as const;
export type StockCountStatus = keyof typeof STOCK_COUNT_STATUSES;

/** domyślny próg nieaktualnej ceny zakupu (dni) — ustawienie „Cena nieaktualna po…” */
export const DEFAULT_PRICE_STALE_DAYS = 180;

/** Ilość w jednostce magazynowej: 3 miejsca po przecinku (12,5 m; 0,125 kg). */
export const roundQty = (q: number) => Math.round(q * 1000) / 1000;

/** Przeliczenie ilości w opakowaniach na jednostkę magazynową (2 krążki × 100 m = 200 m). */
export const packToStock = (packQty: number, factor: number) => roundQty(packQty * factor);

/** Cena za jednostkę magazynową z ceny opakowania (krążek 100 m za 250 zł → 2,50 zł/m). Wynik w groszach, z ułamkiem. */
export const packPriceToUnit = (packPriceGr: number, factor: number) => Math.round((packPriceGr / factor) * 100) / 100;

const str = (max: number) => z.string().trim().max(max);
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const qtyVal = z.number().refine((v) => Number.isFinite(v) && Math.abs(v) <= 10_000_000, 'Nieprawidłowa ilość');

// ───────── magazyny ─────────

export const warehouseInputSchema = z.object({
  companyId: z.string().uuid(),
  code: str(10).min(1, 'Podaj kod magazynu').regex(/^[A-Z0-9-]+$/i, 'Kod: litery, cyfry, myślnik'),
  name: str(100).min(2, 'Podaj nazwę magazynu'),
  address: str(300).default(''),
  pickupNote: str(1000).default(''),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});
export type WarehouseInput = z.infer<typeof warehouseInputSchema>;

export interface WarehouseDto {
  id: string;
  companyId: string;
  companyCode: string;
  code: string;
  name: string;
  address: string;
  pickupNote: string;
  active: boolean;
  isDefault: boolean;
}

export const userWarehousesSchema = z.object({ warehouseIds: z.array(z.string().uuid()).max(100) });

// ───────── stany ─────────

export interface StockLevelDto {
  warehouseId: string;
  itemId: string;
  code: string;
  name: string;
  unit: string;
  kind: string;
  groupId: string | null;
  groupName: string | null;
  tradeCodes: string[];
  onHand: number;
  reserved: number;
  picked: number;
  /** dostępne do kolejnych rezerwacji = fizycznie − zarezerwowane */
  available: number;
  /** oczekiwane w złożonych zamówieniach (etap 4) */
  incoming: number;
  minQty: number | null;
  belowMin: boolean;
  location: string;
  /** średnia cena (PLN gr / jedn.) i wartość — tylko z ceny.zakup */
  avgCostGr: number | null;
  valueGr: number | null;
}

export const stockQuerySchema = z.object({
  warehouseId: z.string().uuid().optional(),
  q: str(100).optional(),
  groupId: z.string().uuid().optional(),
  trade: str(4).optional(),
  filter: z.enum(['wszystkie', 'na_stanie', 'ponizej_minimum', 'zarezerwowane']).default('wszystkie'),
});
export type StockQuery = z.infer<typeof stockQuerySchema>;

export const stockPlaceSchema = z.object({
  location: str(100).default(''),
  minQty: z.number().min(0).max(10_000_000).nullable().default(null),
});

export interface StockMovementDto {
  id: number;
  at: string;
  documentId: string;
  documentNumber: string | null;
  kind: StockDocKind;
  warehouseId: string;
  warehouseCode: string;
  quantity: number;
  /** tylko z ceny.zakup */
  unitCostGr: number | null;
  valueGr: number | null;
  caseNumber: string | null;
  teamName: string | null;
  byName: string | null;
}

export interface StockItemDetailDto {
  item: { id: string; code: string; name: string; unit: string; ean: string; groupName: string | null; stocked: boolean };
  levels: StockLevelDto[];
  movements: StockMovementDto[];
}

// ───────── dokumenty ─────────

export const stockLineInputSchema = z.object({
  itemId: z.string().uuid(),
  /** opcjonalnie w opakowaniach: ilość opakowań × przelicznik = ilość w jednostce magazynowej */
  packId: z.string().uuid().nullable().default(null),
  packQty: z.number().positive().max(10_000_000).nullable().default(null),
  /** w jednostce magazynowej; dla KOR ze znakiem (+ nadwyżka, − niedobór) */
  quantity: qtyVal,
  /** cena za jednostkę magazynową w groszach waluty dokumentu (BO, PZ, KOR+); może mieć ułamek grosza */
  unitPriceGr: z.number().min(0).max(100_000_000_00).nullable().default(null),
  note: str(300).default(''),
});
export type StockLineInput = z.infer<typeof stockLineInputSchema>;

export const stockDocumentInputSchema = z.object({
  kind: z.enum(MANUAL_DOC_KINDS as [StockDocKind, ...StockDocKind[]]),
  warehouseId: z.string().uuid(),
  targetWarehouseId: z.string().uuid().nullable().default(null),
  docDate: day,
  reason: str(1000).default(''),
  note: str(2000).default(''),
  counterparty: str(200).default(''),
  externalNumber: str(80).default(''),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Waluta: kod ISO, np. PLN, EUR').default('PLN'),
  exchangeRate: z.number().positive().max(100_000).default(1),
  caseId: z.string().uuid().nullable().default(null),
  teamId: z.string().uuid().nullable().default(null),
  recipientUserId: z.string().uuid().nullable().default(null),
  sourceDocumentId: z.string().uuid().nullable().default(null),
  lines: z.array(stockLineInputSchema).min(1, 'Dodaj co najmniej jedną pozycję').max(500),
  /** zatwierdź od razu (zmienia stan) */
  approve: z.boolean().default(false),
  /** klucz idempotencji z formularza — powtórzone żądanie zwraca ten sam dokument */
  requestKey: str(80).min(8).nullable().default(null),
});
export type StockDocumentInput = z.infer<typeof stockDocumentInputSchema>;

export const stockReverseSchema = z.object({
  reason: str(1000).min(5, 'Podaj przyczynę odwrócenia (co najmniej 5 znaków)'),
  requestKey: str(80).min(8).nullable().default(null),
});

export const stockDocumentQuerySchema = z.object({
  warehouseId: z.string().uuid().optional(),
  companyId: z.string().uuid().optional(),
  kind: z.enum(STOCK_DOC_KIND_KEYS as [StockDocKind, ...StockDocKind[]]).optional(),
  status: z.enum(['roboczy', 'zatwierdzony']).optional(),
  q: str(100).optional(),
  from: day.optional(),
  to: day.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type StockDocumentQuery = z.infer<typeof stockDocumentQuerySchema>;

export interface StockDocumentLineDto {
  id: string;
  position: number;
  itemId: string;
  code: string;
  name: string;
  unit: string;
  packId: string | null;
  packName: string | null;
  packQty: number | null;
  quantity: number;
  /** cena w walucie dokumentu — tylko z ceny.zakup */
  unitPriceGr: number | null;
  /** wartość ruchu w PLN (po zatwierdzeniu) — tylko z ceny.zakup */
  valueGr: number | null;
  note: string;
}

export interface StockDocumentListItemDto {
  id: string;
  kind: StockDocKind;
  number: string | null;
  status: StockDocStatus;
  docDate: string;
  companyId: string;
  companyCode: string;
  warehouseId: string;
  warehouseCode: string;
  targetWarehouseCode: string | null;
  counterparty: string;
  caseNumber: string | null;
  teamName: string | null;
  lineCount: number;
  valueGr: number | null;
  reversalOf: string | null;
  reversedBy: { id: string; number: string | null } | null;
  createdByName: string | null;
  createdAt: string;
}

export interface StockDocumentDto extends StockDocumentListItemDto {
  targetWarehouseId: string | null;
  reason: string;
  note: string;
  externalNumber: string;
  currency: string;
  exchangeRate: number;
  caseId: string | null;
  teamId: string | null;
  recipientUserId: string | null;
  recipientName: string | null;
  sourceDocumentId: string | null;
  sourceDocumentNumber: string | null;
  reversalOfNumber: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  lines: StockDocumentLineDto[];
  canEdit: boolean;
  canApprove: boolean;
  canReverse: boolean;
}

export interface StockDocumentListDto {
  rows: StockDocumentListItemDto[];
  total: number;
}

/** Dopasowanie kodów z importu CSV stanu początkowego (kod / EAN → produkt). */
export const resolveCodesSchema = z.object({ codes: z.array(str(40).min(1)).min(1).max(2000) });
export interface ResolvedCodeDto {
  code: string;
  itemId: string | null;
  name: string | null;
  unit: string | null;
  stocked: boolean;
}

// ───────── inwentaryzacja ─────────

export const stockCountCreateSchema = z.object({
  warehouseId: z.string().uuid(),
  groupId: z.string().uuid().nullable().default(null),
  note: str(500).default(''),
});

export const stockCountLinesSchema = z.object({
  lines: z
    .array(z.object({ itemId: z.string().uuid(), counted: z.number().min(0).max(10_000_000).nullable(), note: str(300).default('') }))
    .min(1)
    .max(2000),
});

export const stockCountCloseSchema = z.object({ reason: str(1000).min(5, 'Podaj przyczynę różnic (co najmniej 5 znaków)') });

export interface StockCountLineDto {
  itemId: string;
  code: string;
  name: string;
  unit: string;
  expected: number;
  /** stan w systemie teraz (różnica liczona od niego przy zamknięciu) */
  current: number;
  counted: number | null;
  difference: number | null;
  note: string;
}

export interface StockCountDto {
  id: string;
  companyId: string;
  warehouseId: string;
  warehouseCode: string;
  groupName: string | null;
  status: StockCountStatus;
  note: string;
  documentId: string | null;
  documentNumber: string | null;
  createdByName: string | null;
  createdAt: string;
  closedAt: string | null;
  countedLines: number;
  totalLines: number;
  lines?: StockCountLineDto[];
}

// ───────── ustawienia ─────────

export const DEMAND_TRIGGERS = {
  podpis: 'Po podpisaniu umowy',
  warunki: 'Po spełnieniu warunków startu (podpis i zaliczka albo odstępstwo)',
  reczne: 'Ręcznie — magazynier tworzy potrzeby z umowy',
} as const;
export type DemandTrigger = keyof typeof DEMAND_TRIGGERS;

export const warehouseSettingsSchema = z.object({
  priceStaleDays: z.number().int().min(7, 'Co najmniej 7 dni').max(3650),
  /** kiedy powstają potrzeby z umowy — per firma (id firmy → wyzwalacz); brak = po podpisaniu */
  demandTriggers: z.record(z.string().uuid(), z.enum(['podpis', 'warunki', 'reczne'])).default({}),
  /** ile dni roboczych przed pierwszym dniem realizacji materiał ma być gotowy do odbioru */
  pickupLeadDays: z.number().int().min(0).max(10).default(1),
});
export type WarehouseSettings = z.infer<typeof warehouseSettingsSchema>;
